import { useState, FormEvent } from 'react';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt') as string;
    if (honeypot && honeypot.trim()) {
      setStatus('success');
      return;
    }

    formData.delete('company_alt');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: formData,
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '';

      if (response.ok && code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('Network error. Please try again.');
    }
  };

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="text-center mb-12 md:mb-16">
          <p className="text-primary-500 font-label text-sm tracking-[0.15em] uppercase mb-3">
            Get in Touch
          </p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-4">
            Contact Us
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto text-base">
            Have a question, a special request, or just want to say hello? We
            would love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto">
          <div className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/60">
            <form
              data-readdy-form="contact-ghalib"
              action="https://readdy.ai/api/form/d9vj9te859p3u981dr4g"
              method="POST"
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="hp-field">
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-foreground-800 mb-1.5"
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-foreground-800 mb-1.5"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-foreground-800 mb-1.5"
                >
                  Phone (Optional)
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label
                  htmlFor="subject"
                  className="block text-sm font-medium text-foreground-800 mb-1.5"
                >
                  Subject
                </label>
                <select
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                >
                  <option value="">Select a topic</option>
                  <option value="general">General Inquiry</option>
                  <option value="reservation">Table Reservation</option>
                  <option value="order">Order Question</option>
                  <option value="feedback">Feedback</option>
                  <option value="partnership">Partnership</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-foreground-800 mb-1.5"
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 rounded-md border border-background-300/60 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="How can we help you?"
                />
                <p className="text-foreground-500 text-xs mt-1">
                  Maximum 500 characters
                </p>
              </div>

              <button
                type="submit"
                className="w-full px-6 py-3.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors"
              >
                Send Message
              </button>

              {status === 'success' && (
                <div className="p-3 bg-primary-100 text-primary-800 text-sm rounded-md">
                  Thank you! Your message has been sent. We will get back to you
                  soon.
                </div>
              )}
              {status === 'error' && (
                <div className="p-3 bg-red-100 text-red-800 text-sm rounded-md">
                  {formError}
                </div>
              )}
            </form>
          </div>

          <div className="space-y-6">
            <div className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/60">
              <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-4">
                Connect With Us
              </h3>
              <div className="space-y-4">
                <a
                  href="tel:+15551234567"
                  className="flex items-center gap-3 text-foreground-700 hover:text-primary-600 transition-colors"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-phone-line" />
                  </div>
                  <span className="text-sm">(555) 123-4567</span>
                </a>
                <a
                  href="mailto:hello@ghalibchai.com"
                  className="flex items-center gap-3 text-foreground-700 hover:text-primary-600 transition-colors"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-mail-line" />
                  </div>
                  <span className="text-sm">hello@ghalibchai.com</span>
                </a>
                <div className="flex items-center gap-3 text-foreground-700">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-map-pin-line" />
                  </div>
                  <span className="text-sm">
                    123 Artisan Lane, Cultural District
                    <br />
                    City Center, CA 90210
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/60">
              <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-4">
                Follow Us
              </h3>
              <div className="flex items-center gap-3">
                {[
                  { icon: 'ri-instagram-line', label: 'Instagram' },
                  { icon: 'ri-facebook-circle-line', label: 'Facebook' },
                  { icon: 'ri-twitter-x-line', label: 'Twitter' },
                  { icon: 'ri-youtube-line', label: 'YouTube' },
                ].map((social) => (
                  <a
                    key={social.label}
                    href="#"
                    aria-label={social.label}
                    className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition-colors"
                  >
                    <i className={`${social.icon} text-lg`} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}